import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, Sparkles, Wand, Car, Zap } from 'lucide-react';
import { SERVICES_DATA, PACKAGES_DATA } from '../data/mockData';
import ServiceCard from '../components/ServiceCard';
import PackageCard from '../components/PackageCard';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const getIcon = useCallback((iconName) => {
    const icons = {
      shield: Shield,
      sparkles: Sparkles,
      wand: Wand,
      car: Car,
      zap: Zap
    };
    const IconComponent = icons[iconName] || Shield;
    return <IconComponent className="w-8 h-8" />;
  }, []);

  // Helper function to render pricing based on service type
  const renderPricing = useCallback((service) => {
    if (service.pricing?.small && service.pricing?.large) {
      return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Small/Medium Vehicles</span>
            <span className="text-[#B8860B] font-bold text-lg">${service.pricing.small}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Large/Oversized Vehicles</span>
            <span className="text-[#B8860B] font-bold text-lg">${service.pricing.large}</span>
          </div>
        </div>
      );
    }
    
    if (service.packages) {
      return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <h4 className="text-white font-semibold mb-3">Package Options:</h4>
          {service.packages.map((pkg) => (
            <div key={pkg.name} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
              <div>
                <span className="text-gray-300 font-medium">{pkg.name}</span>
                {pkg.popular && (
                  <span className="ml-2 text-xs bg-[#B8860B] text-[#111216] px-2 py-0.5 rounded-full font-bold">POPULAR</span>
                )}
              </div>
              <span className="text-[#B8860B] font-bold text-lg">${pkg.price}</span>
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">{service.pricingNote}</span>
          <span className="text-[#B8860B] font-bold text-2xl">{service.priceRange}</span>
        </div>
      </div>
    );
  }, []);

  const handleServiceToggle = useCallback((serviceId) => {
    setSelectedService(prev => prev === serviceId ? null : serviceId);
  }, []);

  return (
    <div className="min-h-screen bg-[#111216] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#B8860B]">Services</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            From ceramic coatings to complete interior restoration, we provide premium detailing services 
            tailored to your vehicle's unique needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {SERVICES_DATA.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                isSelected={selectedService === service.id}
                onToggle={() => handleServiceToggle(service.id)}
                renderPricing={renderPricing}
                getIcon={getIcon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Complete <span className="text-[#B8860B]">Packages</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Save with our comprehensive detailing packages designed for complete vehicle care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {PACKAGES_DATA.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Popular <span className="text-[#B8860B]">Add-Ons</span>
            </h2>
            <p className="text-gray-400 text-lg">Enhance your service with these premium extras</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              { id: 'headlight-restoration', name: 'Headlight Restoration', price: 120, priceRange: null },
              { id: 'pet-hair-removal', name: 'Pet Hair Removal', price: null, priceRange: '$25 - $50' },
              { id: 'odor-elimination', name: 'Odor Elimination', price: 99, priceRange: null },
              { id: 'engine-bay-detail', name: 'Engine Bay Detail', price: 99, priceRange: null }
            ].map((addon) => (
              <div
                key={addon.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all"
              >
                <h4 className="text-white font-semibold text-lg mb-2">{addon.name}</h4>
                {addon.priceRange ? (
                  <p className="text-[#B8860B] font-bold text-2xl">{addon.priceRange}</p>
                ) : (
                  <p className="text-[#B8860B] font-bold text-2xl">+${addon.price}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111216] mb-4">
            Not Sure Which Service?
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Call us for a personalized recommendation based on your vehicle's needs
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/booking">
              <Button
                size="lg"
                className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold"
              >
                Book Consultation
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#111216] text-[#111216] hover:bg-[#111216] hover:text-white font-semibold"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
